import { Colors, Img, Lightning, Router, Storage, Utils } from "@lightningjs/sdk";
import { TranslatableText } from "../../components/data-display/TranslatableText/TranslatableText";
import { Button } from "../../components/input/Button/Button";
import theme from "../../lib/theme";
import { STORAGE_KEYS, getHomePage, isPostTvLinux, isPostTvAndroid } from "../../lib/utils";
import { postTvWebSocket } from "../../lib/websocket";
import { updateParentalPin } from "../../api/mutations";
import { refreshUserData } from "../../lib/utils";

const AGE_OPTIONS = [0, 6, 10, 12, 16, 18];
const AGE_BOX_SIZE = 56;
const AGE_BOX_SPACING = 12;
const PIN_BOX_SIZE = 56;
const PIN_BOX_SPACING = 12;
const PIN_DIGIT_COUNT = 4;

interface ParentalSetupTemplateSpec extends Lightning.Component.TemplateSpec {
    Welcome: object;
    PinSetup: object;
    AgeSelect: object;
    SuccessView: object;
}

interface ParentalSetupTypeConfig extends Lightning.Component.TypeConfig {
    IsPage: true;
}

type ViewState = "welcome" | "pin" | "age" | "success";

export class ParentalSetup
    extends Lightning.Component<ParentalSetupTemplateSpec, ParentalSetupTypeConfig>
    implements Lightning.Component.ImplementTemplateSpec<ParentalSetupTemplateSpec>
{
    private _currentView: ViewState = "welcome";

    // PIN state
    private _pin: string[] = [];
    private _pinFocusArea: "boxes" | "continue" = "boxes";
    private _pinMode: "set" | "confirm" = "set";
    private _firstPin?: string;

    // Age state
    private _selectedAgeIndex = 4;
    private _ageFocusArea: "buttons" | "continue" = "buttons";

    static override _template(): Lightning.Component.Template<ParentalSetupTemplateSpec> {
        const pinRowWidth = PIN_BOX_SIZE * PIN_DIGIT_COUNT + PIN_BOX_SPACING * (PIN_DIGIT_COUNT - 1);
        const ageRowWidth = AGE_BOX_SIZE * AGE_OPTIONS.length + AGE_BOX_SPACING * (AGE_OPTIONS.length - 1);

        return {
            w: theme.layout.w,
            h: theme.layout.h,
            rect: true,
            color: Colors(theme.colors.bg.primary).get(),

            // === WELCOME VIEW ===
            Welcome: {
                w: theme.layout.w,
                h: theme.layout.h,
                flex: {
                    direction: "column",
                    alignItems: "center",
                    justifyContent: "center"
                },
                Logo: {
                    flexItem: { marginBottom: 40 },
                    w: theme.logo.w,
                    h: theme.logo.h,
                    texture: Img(Utils.asset("images/logo.png")).contain(theme.logo.w, theme.logo.h)
                },
                Title: {
                    flexItem: { marginBottom: 20 },
                    type: TranslatableText,
                    key: "setup.welcome",
                    text: {
                        textAlign: "center",
                        fontFace: "SemiBold",
                        fontSize: 48,
                        textColor: Colors(theme.colors.text.primary).get()
                    }
                },
                Subtitle: {
                    flexItem: { marginBottom: 40 },
                    type: TranslatableText,
                    key: "setup.welcomeSubtitle",
                    text: {
                        textAlign: "center",
                        fontSize: 24,
                        textColor: Colors(theme.colors.text.secondary).get(),
                        wordWrap: true,
                        wordWrapWidth: 600
                    }
                },
                ContinueWrapper: {
                    w: 300,
                    h: (theme.button.h || 56) + 2,
                    Border: {
                        x: -1, y: -1, w: 302, h: (theme.button.h || 56) + 2,
                        rect: true, color: 0x00000000,
                        shader: { type: Lightning.shaders.RoundedRectangle, radius: theme.button.radius || 8, stroke: 2, strokeColor: 0xffffffff }
                    },
                    ContinueBtn: { type: Button, w: 300, label: "pin.continue", actionId: "welcomeContinue" }
                }
            },

            // === PIN SETUP VIEW ===
            PinSetup: {
                w: theme.layout.w,
                h: theme.layout.h,
                rect: true,
                color: 0xff0d3266,
                alpha: 0,
                visible: false,
                Logo: {
                    x: (w) => w / 2, y: 60, mountX: 0.5,
                    w: theme.logo.w, h: theme.logo.h,
                    texture: Img(Utils.asset("images/logo.png")).contain(theme.logo.w, theme.logo.h)
                },
                Card: {
                    x: (w) => w / 2, y: (h) => h / 2, mount: 0.5,
                    w: 600, h: 380,
                    rect: true,
                    color: 0xff2068c8,
                    shader: { type: Lightning.shaders.RoundedRectangle, radius: 16 },
                    flex: { direction: "column", alignItems: "center", justifyContent: "center", paddingTop: 30, paddingBottom: 30 },
                    Title: {
                        flexItem: { marginBottom: 12 },
                        type: TranslatableText, key: "setup.pinTitle",
                        text: { textAlign: "center", fontFace: "SemiBold", fontSize: 30, textColor: Colors(theme.colors.text.primary).get() }
                    },
                    Subtitle: {
                        flexItem: { marginBottom: 24 },
                        type: TranslatableText, key: "setup.pinSubtitle",
                        text: { textAlign: "center", fontSize: 20, textColor: Colors(theme.colors.text.secondary).get(), wordWrap: true, wordWrapWidth: 500 }
                    },
                    Label: {
                        flexItem: { marginBottom: 12 },
                        type: TranslatableText, key: "setup.pinLabel",
                        text: { textAlign: "center", fontFace: "SemiBold", fontSize: 18, textColor: Colors(theme.colors.text.primary).get() }
                    },
                    PinBoxes: {
                        flexItem: { marginBottom: 16 },
                        w: pinRowWidth, h: PIN_BOX_SIZE,
                        flex: { direction: "row", alignItems: "center" },
                        ...Object.fromEntries(
                            Array.from({ length: PIN_DIGIT_COUNT }, (_, i) => [
                                `Pin${i}`,
                                {
                                    w: PIN_BOX_SIZE, h: PIN_BOX_SIZE,
                                    flexItem: i < PIN_DIGIT_COUNT - 1 ? { marginRight: PIN_BOX_SPACING } : {},
                                    rect: true, color: 0xffffffff,
                                    shader: { type: Lightning.shaders.RoundedRectangle, radius: 8, stroke: 0, strokeColor: 0xffffffff },
                                    Label: {
                                        x: PIN_BOX_SIZE / 2, y: PIN_BOX_SIZE / 2 + 3, mountX: 0.5, mountY: 0.5,
                                        text: { text: "0", fontSize: 26, fontFace: "SemiBold", textColor: 0xff1565c0, textAlign: "center" }
                                    }
                                }
                            ])
                        )
                    },
                    Error: {
                        flexItem: { marginBottom: 8 },
                        type: TranslatableText, key: "pin.mismatch",
                        text: { textAlign: "center", fontSize: 16, textColor: Colors("#ff4444").get() },
                        alpha: 0
                    },
                    PinContinueWrapper: {
                        w: 300, h: (theme.button.h || 56) + 2,
                        Border: {
                            x: -1, y: -1, w: 302, h: (theme.button.h || 56) + 2,
                            rect: true, color: 0x00000000,
                            shader: { type: Lightning.shaders.RoundedRectangle, radius: theme.button.radius || 8, stroke: 2, strokeColor: 0xffffffff }
                        },
                        PinContinueBtn: { type: Button, w: 300, label: "pin.continue", actionId: "pinContinue" }
                    }
                }
            },

            // === AGE SELECT VIEW ===
            AgeSelect: {
                w: theme.layout.w,
                h: theme.layout.h,
                rect: true,
                color: 0xff0d3266,
                alpha: 0,
                visible: false,
                Logo: {
                    x: (w) => w / 2, y: 60, mountX: 0.5,
                    w: theme.logo.w, h: theme.logo.h,
                    texture: Img(Utils.asset("images/logo.png")).contain(theme.logo.w, theme.logo.h)
                },
                Card: {
                    x: (w) => w / 2, y: (h) => h / 2, mount: 0.5,
                    w: 600, h: 380,
                    rect: true,
                    color: 0xff2068c8,
                    shader: { type: Lightning.shaders.RoundedRectangle, radius: 16 },
                    flex: { direction: "column", alignItems: "center", justifyContent: "center", paddingTop: 30, paddingBottom: 30 },
                    Title: {
                        flexItem: { marginBottom: 12 },
                        type: TranslatableText, key: "setup.ageTitle",
                        text: { textAlign: "center", fontFace: "SemiBold", fontSize: 30, textColor: Colors(theme.colors.text.primary).get() }
                    },
                    Subtitle: {
                        flexItem: { marginBottom: 24 },
                        type: TranslatableText, key: "setup.ageSubtitle",
                        text: { textAlign: "center", fontSize: 20, textColor: Colors(theme.colors.text.secondary).get(), wordWrap: true, wordWrapWidth: 500 }
                    },
                    Label: {
                        w: AGE_BOX_SIZE * AGE_OPTIONS.length + AGE_BOX_SPACING * (AGE_OPTIONS.length - 1),
                        flexItem: { marginBottom: 12 },
                        type: TranslatableText, key: "setup.ageLabel",
                        text: { textAlign: "left", fontFace: "SemiBold", fontSize: 18, textColor: Colors(theme.colors.text.primary).get() }
                    },
                    AgeButtons: {
                        flexItem: { marginBottom: 10 },
                        w: AGE_BOX_SIZE * AGE_OPTIONS.length + AGE_BOX_SPACING * (AGE_OPTIONS.length - 1),
                        h: AGE_BOX_SIZE,
                        flex: { direction: "row", alignItems: "center" },
                        ...Object.fromEntries(
                            AGE_OPTIONS.map((age, i) => [
                                `Age${i}`,
                                {
                                    w: AGE_BOX_SIZE, h: AGE_BOX_SIZE,
                                    flexItem: i < AGE_OPTIONS.length - 1 ? { marginRight: AGE_BOX_SPACING } : {},
                                    Background: {
                                        x: 0, y: 0,
                                        texture: Lightning.Tools.getRoundRect(
                                            AGE_BOX_SIZE - 2, AGE_BOX_SIZE - 2, 12, 1, 0x80ffffff, false, 0x00000000
                                        )
                                    },
                                    Label: {
                                        x: AGE_BOX_SIZE / 2, y: AGE_BOX_SIZE / 2, mountX: 0.5, mountY: 0.5,
                                        text: { text: `${age}+`, fontSize: 22, fontFace: "Regular", textColor: 0xffffffff, textAlign: "center" }
                                    }
                                }
                            ])
                        )
                    },
                    AgeDescription: {
                        flexItem: { marginBottom: 16 },
                        w: AGE_BOX_SIZE * AGE_OPTIONS.length + AGE_BOX_SPACING * (AGE_OPTIONS.length - 1),
                        text: { text: "", fontSize: 16, textColor: Colors(theme.colors.text.secondary).get() }
                    },
                    AgeContinueWrapper: {
                        w: 300, h: (theme.button.h || 56) + 2,
                        Border: {
                            x: -1, y: -1, w: 302, h: (theme.button.h || 56) + 2,
                            rect: true, color: 0x00000000,
                            shader: { type: Lightning.shaders.RoundedRectangle, radius: theme.button.radius || 8, stroke: 2, strokeColor: 0xffffffff }
                        },
                        AgeContinueBtn: { type: Button, w: 300, label: "setup.ageContinue", actionId: "ageContinue" }
                    }
                }
            },

            // === SUCCESS VIEW ===
            SuccessView: {
                w: theme.layout.w,
                h: theme.layout.h,
                alpha: 0,
                visible: false,
                flex: { direction: "column", alignItems: "center", justifyContent: "center" },
                Logo: {
                    flexItem: { marginBottom: 60 },
                    w: theme.logo.w, h: theme.logo.h,
                    texture: Img(Utils.asset("images/logo.png")).contain(theme.logo.w, theme.logo.h)
                },
                Title: {
                    flexItem: { marginBottom: 16 },
                    type: TranslatableText, key: "setup.successTitle",
                    text: { textAlign: "center", fontFace: "SemiBold", fontSize: 36, textColor: Colors(theme.colors.text.primary).get() }
                },
                Message: {
                    flexItem: { marginBottom: 40 },
                    type: TranslatableText, key: "setup.successSubtitle",
                    text: { textAlign: "center", fontSize: 22, textColor: Colors(theme.colors.text.secondary).get(), wordWrap: true, wordWrapWidth: 500 }
                },
                SuccessContinueWrapper: {
                    w: 300, h: (theme.button.h || 56) + 2,
                    Border: {
                        x: -1, y: -1, w: 302, h: (theme.button.h || 56) + 2,
                        rect: true, color: 0x00000000,
                        shader: { type: Lightning.shaders.RoundedRectangle, radius: theme.button.radius || 8, stroke: 2, strokeColor: 0xffffffff }
                    },
                    SuccessContinueBtn: { type: Button, w: 300, label: "pin.continue", actionId: "successContinue" }
                }
            }
        };
    }

    readonly Welcome = this.getByRef("Welcome")!;
    readonly PinSetup = this.getByRef("PinSetup")!;
    readonly AgeSelect = this.getByRef("AgeSelect")!;
    readonly SuccessView = this.getByRef("SuccessView")!;

    override _active() {
        this._currentView = "welcome";
        this._pin = [];
        this._pinMode = "set";
        this._firstPin = undefined;
        this._selectedAgeIndex = 4;
        this._ageFocusArea = "buttons";
        this._pinFocusArea = "boxes";
        this.Welcome.patch({ alpha: 1, visible: true });
        this.PinSetup.patch({ alpha: 0, visible: false });
        this.AgeSelect.patch({ alpha: 0, visible: false });
        this.SuccessView.patch({ alpha: 0, visible: false });
    }

    override _getFocused() {
        if (this._currentView === "welcome") {
            const wrapper = (this.Welcome as any).getByRef("ContinueWrapper");
            return wrapper?.getByRef("ContinueBtn") || this;
        }
        if (this._currentView === "pin" && this._pinFocusArea === "continue") {
            const card = (this.PinSetup as any).getByRef("Card");
            const wrapper = card?.getByRef("PinContinueWrapper");
            return wrapper?.getByRef("PinContinueBtn") || this;
        }
        if (this._currentView === "age" && this._ageFocusArea === "continue") {
            const card = (this.AgeSelect as any).getByRef("Card");
            const wrapper = card?.getByRef("AgeContinueWrapper");
            return wrapper?.getByRef("AgeContinueBtn") || this;
        }
        if (this._currentView === "success") {
            const wrapper = (this.SuccessView as any).getByRef("SuccessContinueWrapper");
            return wrapper?.getByRef("SuccessContinueBtn") || this;
        }
        return this;
    }

    override _handleBack() {
        return true;
    }

    override _captureKey(e: KeyboardEvent) {
        // === WELCOME ===
        if (this._currentView === "welcome" && (e.key === "Enter" || e.keyCode === 13)) {
            this._showPinSetup();
            return true;
        }

        // === PIN SETUP ===
        if (this._currentView === "pin") {
            if (this._pinFocusArea === "boxes") {
                const numKey = +e.key;
                if (!isNaN(numKey) && numKey >= 0 && numKey <= 9 && this._pin.length < PIN_DIGIT_COUNT) {
                    this._pin.push(e.key);
                    this._updatePinBoxes();
                    if (this._pin.length === PIN_DIGIT_COUNT) {
                        this._pinFocusArea = "continue";
                        this._refocus();
                    }
                    return true;
                }
                if (e.key === "Backspace" || e.key === "Delete") {
                    if (this._pin.length > 0) {
                        this._pin.pop();
                        this._updatePinBoxes();
                    }
                    return true;
                }
                if (e.key === "ArrowDown" && this._pin.length === PIN_DIGIT_COUNT) {
                    this._pinFocusArea = "continue";
                    this._refocus();
                    return true;
                }
            }
            if (this._pinFocusArea === "continue") {
                if (e.key === "ArrowUp") {
                    this._pinFocusArea = "boxes";
                    this._refocus();
                    return true;
                }
                if (e.key === "Enter" || e.keyCode === 13) {
                    this._submitPin();
                    return true;
                }
                if (e.key === "Backspace" || e.key === "Delete") {
                    if (this._pin.length > 0) {
                        this._pin.pop();
                        this._updatePinBoxes();
                        this._pinFocusArea = "boxes";
                        this._refocus();
                    }
                    return true;
                }
            }
            return true;
        }

        // === AGE SELECT ===
        if (this._currentView === "age") {
            if (this._ageFocusArea === "buttons") {
                if (e.key === "ArrowLeft" && this._selectedAgeIndex > 0) {
                    this._selectedAgeIndex--;
                    this._updateAgeSelection();
                    return true;
                }
                if (e.key === "ArrowRight" && this._selectedAgeIndex < AGE_OPTIONS.length - 1) {
                    this._selectedAgeIndex++;
                    this._updateAgeSelection();
                    return true;
                }
                if (e.key === "ArrowDown" || e.key === "Enter" || e.keyCode === 13) {
                    this._ageFocusArea = "continue";
                    this._refocus();
                    return true;
                }
            }
            if (this._ageFocusArea === "continue") {
                if (e.key === "ArrowUp") {
                    this._ageFocusArea = "buttons";
                    this._refocus();
                    return true;
                }
                if (e.key === "Enter" || e.keyCode === 13) {
                    this._confirmAge();
                    return true;
                }
            }
            return true;
        }

        if (this._currentView === "success") {
            if (e.key === "Enter" || e.keyCode === 13) {
                this._navigateHome();
                return true;
            }
            return true;
        }

        return false;
    }

    $handleBtnClick(id: string) {
        if (id === "welcomeContinue") this._showPinSetup();
        else if (id === "pinContinue") this._submitPin();
        else if (id === "ageContinue") this._confirmAge();
        else if (id === "successContinue") this._navigateHome();
    }

    // === VIEW TRANSITIONS ===

    _showPinSetup() {
        this._currentView = "pin";
        this._pin = [];
        this._pinMode = "set";
        this._firstPin = undefined;
        this._pinFocusArea = "boxes";
        this.Welcome.patch({ alpha: 0, visible: false });
        this.PinSetup.patch({ alpha: 1, visible: true });
        this._setDarkBackground();
        this._updatePinBoxes();
        this._updatePinLabels();
        this._hidePinError();
        this._refocus();
    }

    _showAgeSelect() {
        this._currentView = "age";
        this._selectedAgeIndex = 4;
        this._ageFocusArea = "buttons";
        this.PinSetup.patch({ alpha: 0, visible: false });
        this.AgeSelect.patch({ alpha: 1, visible: true });
        this._updateAgeSelection();
        this._refocus();
    }

    _showSuccess(_messageKey?: string) {
        this._currentView = "success";
        this.PinSetup.patch({ alpha: 0, visible: false });
        this.AgeSelect.patch({ alpha: 0, visible: false });
        this.SuccessView.patch({ alpha: 1, visible: true });
        this._restoreAppBackground();
    }

    // === PIN LOGIC ===

    _submitPin() {
        if (this._pin.length !== PIN_DIGIT_COUNT) return;
        const pin = this._pin.join("");

        if (this._pinMode === "set") {
            this._firstPin = pin;
            this._pinMode = "confirm";
            this._pin = [];
            this._pinFocusArea = "boxes";
            this._updatePinBoxes();
            this._updatePinLabels();
            this._hidePinError();
            this._refocus();
        } else if (this._pinMode === "confirm") {
            if (pin === this._firstPin) {
                this._savePin(pin);
                this._showAgeSelect();
            } else {
                // Mismatch — reset to set mode
                this._pin = [];
                this._firstPin = undefined;
                this._pinMode = "set";
                this._pinFocusArea = "boxes";
                this._updatePinBoxes();
                this._updatePinLabels();
                this._showPinError();
                this._refocus();
            }
        }
    }

    _updatePinBoxes() {
        const card = (this.PinSetup as any).getByRef("Card");
        if (!card) return;
        const boxes = card.getByRef("PinBoxes") as any;
        if (!boxes) return;

        for (let i = 0; i < PIN_DIGIT_COUNT; i++) {
            const box = boxes.getByRef(`Pin${i}`) as any;
            if (!box) continue;
            const label = box.getByRef("Label") as any;
            const hasValue = i < this._pin.length;

            if (hasValue) {
                // Show blue dot for entered digit
                if (label) label.patch({ text: { text: "●", fontSize: 28 } });
            } else if (i === this._pin.length) {
                // Current input position — show underscore
                if (label) label.patch({ text: { text: "_", fontSize: 26 } });
            } else {
                // Empty — show 0
                if (label) label.patch({ text: { text: "0", fontSize: 26 } });
            }
        }
    }

    _updatePinLabels() {
        const card = (this.PinSetup as any).getByRef("Card");
        if (!card) return;
        const title = card.getByRef("Title") as any;
        const label = card.getByRef("Label") as any;

        if (this._pinMode === "set") {
            if (title) title.patch({ key: "setup.pinTitle" });
            if (label) label.patch({ key: "setup.pinLabel" });
        } else {
            if (title) title.patch({ key: "setup.pinConfirmTitle" });
            if (label) label.patch({ key: "setup.pinConfirmLabel" });
        }
    }

    _showPinError() {
        const card = (this.PinSetup as any).getByRef("Card");
        if (!card) return;
        const error = card.getByRef("Error") as any;
        if (error) error.patch({ alpha: 1 });
    }

    _hidePinError() {
        const card = (this.PinSetup as any).getByRef("Card");
        if (!card) return;
        const error = card.getByRef("Error") as any;
        if (error) error.patch({ alpha: 0 });
    }

    // === AGE LOGIC ===

    _updateAgeSelection() {
        const card = (this.AgeSelect as any).getByRef("Card");
        if (!card) return;
        const buttonsContainer = card.getByRef("AgeButtons") as any;
        if (!buttonsContainer) return;

        for (let i = 0; i < AGE_OPTIONS.length; i++) {
            const ageBox = buttonsContainer.getByRef(`Age${i}`) as any;
            if (ageBox) {
                const isSelected = i === this._selectedAgeIndex;
                const bg = ageBox.getByRef("Background") as any;
                if (bg) {
                    if (isSelected) {
                        bg.texture = Lightning.Tools.getRoundRect(
                            AGE_BOX_SIZE - 2, AGE_BOX_SIZE - 2, 12, 0, 0x00000000, true, 0xffffffff
                        );
                    } else {
                        bg.texture = Lightning.Tools.getRoundRect(
                            AGE_BOX_SIZE - 2, AGE_BOX_SIZE - 2, 12, 1, 0x80ffffff, false, 0x00000000
                        );
                    }
                }
                ageBox.patch({
                    smooth: { scale: isSelected ? 1.15 : 1.0 }
                });
                const label = ageBox.getByRef("Label") as any;
                if (label) {
                    label.patch({ text: { fontFace: "Regular", fontSize: isSelected ? 24 : 22, textColor: isSelected ? 0xff1565c0 : 0xffffffff } });
                }
            }
        }

        const desc = card.getByRef("AgeDescription") as any;
        if (desc) {
            const age = AGE_OPTIONS[this._selectedAgeIndex];
            desc.patch({ text: { text: `Ab ${age} Jahren` } });
        }
    }

    _confirmAge() {
        const age = AGE_OPTIONS[this._selectedAgeIndex];
        const ageStr = age < 10 ? `0${age}` : `${age}`;
        this._saveAgeRestriction(ageStr);
        this._showSuccess("pin.ageChangeSuccess");
    }

    // === NAVIGATION ===

    _setDarkBackground() {
        try {
            const canvas = this.stage.getCanvas();
            if (canvas) {
                canvas.style.backgroundColor = "#0d3266";
                if (canvas.parentElement) {
                    canvas.parentElement.style.backgroundColor = "#0d3266";
                }
            }
            document.body.style.backgroundColor = "#0d3266";
        } catch (e) {}
    }

    _restoreAppBackground() {
        const bgColor = theme.colors.bg.primary;
        try {
            const canvas = this.stage.getCanvas();
            if (canvas) {
                canvas.style.backgroundColor = bgColor;
                if (canvas.parentElement) {
                    canvas.parentElement.style.backgroundColor = bgColor;
                }
            }
            document.body.style.backgroundColor = bgColor;
        } catch (e) {}
    }

    _navigateHome() {
        this._restoreAppBackground();
        Router.navigate(getHomePage(), { replace: true });
    }

    _hasPin(): boolean {
        const user = Storage.get(STORAGE_KEYS.user) as any;
        return !!(user?.settings?.parentalPin) || !!Storage.get(STORAGE_KEYS.parentalPin);
    }

    _hasAge(): boolean {
        const user = Storage.get(STORAGE_KEYS.user) as any;
        return !!(user?.settings?.parentalAgeRestriction) || !!Storage.get(STORAGE_KEYS.parentalAgeRestriction);
    }

    // === SAVE LOGIC ===

    async _savePin(pin: string) {
        try {
            await updateParentalPin(pin);
            await refreshUserData();
            Storage.set(STORAGE_KEYS.parentalPin, pin);
            if (isPostTvLinux() && postTvWebSocket.isConnected) {
                try { postTvWebSocket.setSettings("pics.pin", pin, () => {}); } catch (e) {}
            }
            if (isPostTvAndroid()) {
                try {
                    const android = window.Android;
                    // @ts-ignore
                    if (android && typeof android.setPinCode === "function") { android.setPinCode(pin); }
                } catch (e) {}
            }
        } catch (error) {
            Storage.set(STORAGE_KEYS.parentalPin, pin);
        }
    }

    async _saveAgeRestriction(age: string) {
        try {
            const user = Storage.get(STORAGE_KEYS.user) as any;
            const currentPin = user?.settings?.parentalPin || Storage.get(STORAGE_KEYS.parentalPin);
            if (currentPin) {
                await updateParentalPin(currentPin, age);
                await refreshUserData();
            }
            Storage.set(STORAGE_KEYS.parentalAgeRestriction, age);
            if (isPostTvLinux() && postTvWebSocket.isConnected) {
                try { postTvWebSocket.setSettings("pics.age", age, () => {}); } catch (e) {}
            }
            if (isPostTvAndroid()) {
                try {
                    const android = window.Android;
                    // @ts-ignore
                    if (android && typeof android.setAgeRestriction === "function") { android.setAgeRestriction(parseInt(age, 10)); }
                } catch (e) {}
            }
        } catch (error) {
            Storage.set(STORAGE_KEYS.parentalAgeRestriction, age);
        }
    }
}
